import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faHeart,
  faSpinner,
  faClock,
  faSearch,
  faGear,
} from '@fortawesome/free-solid-svg-icons';
import { SpaceContext } from '../../SpaceContext';
import { getFileTypeIcon, isImageFile, getFileExtension } from '../../utils/file';
import folderIcon from '../../assets/icons/folder.svg';
import service from '../../utils/request';
import './LibraryPage.css';

// ── Constants ──────────────────────────────────────────────

// (Sidebar folders are now derived dynamically from GET /topic/counts)

// ── Helpers ────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch {
    return dateStr;
  }
}

function formatSize(bytes) {
  if (bytes == null || bytes === '' || bytes === 0) return '—';
  const b = Number(bytes);
  if (isNaN(b)) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// ── Component ──────────────────────────────────────────────
export default function LibraryPage() {
  // State
  const [topics, setTopics] = useState([]);
  const [files, setFiles] = useState([]); // from POST /topic/filter_files
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [recentFiles, setRecentFiles] = useState([]); // from GET /topic/recent_files
  const [activeTags, setActiveTags] = useState([]); // [{id, context, value}, ...]
  const [expandedFolders, setExpandedFolders] = useState({}); // { folderName: true/false }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('view') || 'list');
  const [loading, setLoading] = useState(true);
  const [countsLoading, setCountsLoading] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [detailPanelsCollapsed, setDetailPanelsCollapsed] = useState(false);
  const [topicCounts, setTopicCounts] = useState({}); // { context: { total, children: [...] } }
  const { coolApps, userProfile } = useContext(SpaceContext);
  const extensions = useMemo(() => {
    const allExtensions = ['txt', 'md'];
    for (const key in coolApps) {
      if (coolApps.hasOwnProperty(key) && coolApps[key]?.extensions) {
        allExtensions.push(...coolApps[key].extensions);
      }
    }
    return allExtensions;
  }, [coolApps]);
  const [showRecent, setShowRecent] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'main', contextName, or null
  const [recentSearchQuery, setRecentSearchQuery] = useState('');
  const [rootTag, setRootTag] = useState(null);


  const isLoggedIn = !!localStorage.getItem('user_info');

  // ── Group topics by context ──
  const topicsByContext = useMemo(() => {
    const map = {};
    for (const t of topics) {
      if (t.context === '') continue; // skip context topic
      if (t.original_topic_id) continue; // skip sub-topic / keywords
      const ctx = t.context;
      if (!map[ctx]) map[ctx] = [];
      map[ctx].push(t);
    }
    return map;
  }, [topics]);

  // ── Fetch files based on active tags ──
  const fetchFiles = useCallback(async (tagIds, currentPage = 1, append = false) => {
    if (!tagIds || tagIds.length === 0) {
      if (!append) {
        setFiles([]);
        setTotalCount(0);
        setHasMore(false);
      }
      return;
    }
    try {
      setLoading(true);
      const result = await service.post('/api/library/filter_files', { topic_ids: tagIds, page: currentPage, limit: 20 });
      if (append) {
        setFiles(prev => {
          const newItems = result?.items || [];
          const existingIds = new Set(prev.map(f => f.item_id));
          return [...prev, ...newItems.filter(f => !existingIds.has(f.item_id))];
        });
      } else {
        setFiles(result?.items || []);
      }
      setTotalCount(result?.total || 0);
      setHasMore((result?.items || []).length === 20);
    } catch (err) {
      console.error('Failed to fetch filtered files:', err);
      if (!append) {
        setFiles([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentFiles = useCallback(async () => {
    try {
      const result = await service.get('/api/library/recent_files');
      setRecentFiles(result || []);
    } catch (err) {
      console.error('Failed to fetch recent files:', err);
    }
  }, []);

  // ── Initial data fetch: topics → find Owner → fetch files ──
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [topicsRes, recentRes] = await Promise.allSettled([
        service.get('/api/topic/'),
        service.get('/api/library/recent_files'),
      ]);

      let allTopics = [];
      if (topicsRes.status === 'fulfilled') {
        allTopics = topicsRes.value || [];
        setTopics(allTopics);
      }

      if (recentRes.status === 'fulfilled' && recentRes.value) {
        setRecentFiles(recentRes.value || []);
      }
      // Find the iGarment topic under Operation context
      const operationGarmentTopic = allTopics.find(t => t.context === 'Operation' && t.name === 'iGarment');

      if (operationGarmentTopic) {
        setRootTag(operationGarmentTopic);
      } else {
        console.warn('[DocumentPage] No default topic found for iGarment');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchInitialData();

    const onFocus = () => {
      fetchRecentFiles();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isLoggedIn, fetchInitialData, fetchRecentFiles]);


  // ── Infinite Scroll Observer (Callback Ref) ──
  const observer = useRef(null);
  const observerTargetRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      if (loading) return;

      if (node && hasMore) {
        observer.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              setPage((prev) => prev + 1);
            }
          },
          { threshold: 0.1 }
        );
        observer.current.observe(node);
      }
    },
    [loading, hasMore]
  );

  // ── Re-fetch when activeTags or rootTag changes ──
  useEffect(() => {
    if (topics.length === 0 || !rootTag) return; // Guard against running before initial data and rootTag are loaded

    let tagIds = [rootTag.id];
    activeTags.forEach((t) => tagIds.push(t.id));

    fetchFiles(tagIds, page, page > 1);

    // Only fetch counts when page is 1 to avoid redundant calls on lazy load
    if (page === 1) {
      setCountsLoading(true);
      service.post('/api/library/counts', { topic_ids: tagIds })
        .then(countsRes => {
          if (countsRes) setTopicCounts(countsRes);
          setCountsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch dynamic counts:', err);
          setCountsLoading(false);
        });
    }
  }, [topics, activeTags, rootTag, fetchFiles, page, topicsByContext]);

  // ── Helper to sort topics by count descending, then name ──
  const getSortedTopics = useCallback((topicsList, context) => {
    if (!topicsList || topicsList.length === 0) return [];
    const childrenCounts = topicCounts[context]?.children || [];
    return [...topicsList].sort((a, b) => {
      const isAActive = activeTags.some(t => t.id === a.id) || (rootTag && rootTag.id === a.id);
      const isBActive = activeTags.some(t => t.id === b.id) || (rootTag && rootTag.id === b.id);

      if (isAActive && !isBActive) return -1;
      if (!isAActive && isBActive) return 1;

      const countA = childrenCounts.find(c => c.id === a.id)?.count || 0;
      const countB = childrenCounts.find(c => c.id === b.id)?.count || 0;
      if (countB !== countA) return countB - countA;
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [topicCounts, activeTags, rootTag]);

  // ── Toggle a tag — single select per context ──
  function toggleTag(topic) {
    setPage(1);
    setHasMore(true);
    setShowRecent(false);
    setActiveTags((prev) => {
      const exists = prev.find((t) => t.id === topic.id);
      if (exists) {
        // Deselect
        return prev.filter((t) => t.id !== topic.id);
      }
      // Replace existing tag in the same context in-place to keep column order fixed
      const ctxIndex = prev.findIndex((t) => t.context === topic.context);
      if (ctxIndex !== -1) {
        const next = [...prev];
        next[ctxIndex] = topic;
        return next;
      }
      // Add new tag at the end
      return [...prev, topic];
    });
  }

  function handleShowRecent() {
    setShowRecent(!showRecent);
    setActiveTags([]);
    setSelectedIds(new Set());
  }

  function removeTag(tagId) {
    setPage(1);
    setActiveTags((prev) => prev.filter((t) => t.id !== tagId));
  }

  function toggleFolder(context, isCurrentlyDefaultExpanded = false) {
    setExpandedFolders((prev) => {
      const current = prev[context] !== undefined ? prev[context] : isCurrentlyDefaultExpanded;
      const next = {};
      Object.keys(topicsByContext).forEach(k => {
        next[k] = false;
      });
      next[context] = !current;
      return next;
    });
  }

  // ── Selection helpers ──

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }


  // ── Toolbar action handlers ──

  async function handleOpenFile(file) {
    if (!file) return;
    const { item_id, item_name, item_type } = file;
    const extension = (item_type ? item_type.replace(/^\./, '') : getFileExtension(item_name)).toLowerCase();

    if (extensions.includes(extension)) {
      try {
        const res = await service.get('/api/library/edit/' + item_id);
        if (res.editor && res.wopisrc && res.access) {
          window.open(`${res.editor}?WOPISrc=${encodeURIComponent(`${res.wopisrc}/${item_id}?access_token=${res.access}`)}`);
          return;
        }
      } catch (e) {
        if (e == "Error: Access denied") {
          alert(e);
          return;
        }
      }
    }

    window.open(`/api/library/open/${item_id}`);
  }

  return (
    <div className="doc-page">
      {/* ── Breadcrumb Dropdown Bar ── */}
      <div className="doc-breadcrumb-bar">

        {/* Root Node */}
        <div className="doc-root-node-container">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'main' ? null : 'main')}
            className="doc-root-node-btn"
          >
            {showRecent ? (
              <><FontAwesomeIcon icon={faClock} /> Recent Files</>
            ) : rootTag ? (
              `${rootTag.context}: ${rootTag.name}`
            ) : (
              `Operation: iGarment`
            )}
          </button>

          {activeDropdown === 'main' && (
            <>
              <div className="doc-dropdown-backdrop" onClick={() => setActiveDropdown(null)}></div>
              <div className="doc-root-dropdown">
                <div className="doc-root-dropdown__header">
                  {(rootTag || showRecent) ? (
                    <button onClick={() => { setRootTag(null); setActiveDropdown(null); setShowRecent(false); setPage(1); setHasMore(true); }} className="doc-root-dropdown__header-btn">Operation: iGarment</button>
                  ) : (
                    <span className="doc-root-dropdown__header-label">Operation: iGarment</span>
                  )}
                </div>
                <div className="doc-root-dropdown__body">
                  <div
                    onClick={() => { handleShowRecent(); setActiveDropdown(null); }}
                    className={`doc-root-dropdown__recent-btn ${showRecent ? 'doc-root-dropdown__recent-btn--active' : ''}`}
                  >
                    <FontAwesomeIcon icon={faClock} className="doc-root-dropdown__recent-icon" /> Recent Files
                  </div>
                  <ul className="doc-tree">
                    {Object.entries(topicsByContext)
                      .sort(([a], [b]) => {
                        if (a === 'Operation') return -1;
                        if (b === 'Operation') return 1;
                        return a.localeCompare(b);
                      })
                      .map(([context, children], index) => {
                        const isExpanded = expandedFolders[context] !== undefined ? expandedFolders[context] : index === 0;
                        const dynamicTotal = countsLoading ? '??' : (topicCounts[context]?.total || 0);
                        return (
                          <li key={context} className="doc-tree__group">
                            <div
                              className="doc-tree__header"
                              onClick={() => toggleFolder(context, index === 0)}
                            >
                              <div className="doc-tree__header-title">
                                <span>{context}</span>
                                <span className="doc-tree__header-count">({dynamicTotal})</span>
                              </div>
                              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="doc-tree__header-chevron" />
                            </div>
                            {isExpanded && (
                              <ul className="doc-tree__children">
                                {getSortedTopics(children, context).map(child => {
                                  const childDynamic = countsLoading ? null : ((topicCounts[context]?.children || []).find(c => c.id === child.id)?.count || 0);
                                  const isActiveRoot = rootTag && rootTag.id === child.id;
                                  return (
                                    <li
                                      key={child.id}
                                      className={`doc-tree__child ${isActiveRoot ? 'doc-tree__child--active' : ''}`}
                                      onClick={() => {
                                        setRootTag(child);
                                        setActiveTags(prev => prev.filter(t => t.context !== child.context));
                                        setActiveDropdown(null);
                                        setShowRecent(false);
                                        setPage(1);
                                        setHasMore(true);
                                      }}
                                    >
                                      {child.name}
                                      {childDynamic !== null && <span className="doc-tree__count"> ({childDynamic})</span>}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Admin Setting Button */}
        {userProfile?.role_id === 2 && (
          <button
            onClick={() => { window.location.href = '/setting/topics'; }}
            className="doc-setting-btn"
            title="Settings"
            aria-label="Settings"
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="doc-body">
        {activeTags.length > 0 && (
          <>
            <div className={`doc-detail-panels-scroll ${detailPanelsCollapsed ? 'doc-detail-panels-scroll--collapsed' : ''}`}>
              {/* Selected topic detail panels */}
              {activeTags.slice(0, 3).map((tag) => {
                const siblings = topicsByContext[tag.context] || [];
                return (
                  <div className="doc-detail-panel" key={tag.context}>
                    <div className="doc-detail-panel__header">
                      <div>
                        {tag.context} {!countsLoading && <span className="doc-detail-panel__header-count"> ({topicCounts[tag.context]?.total || 0})</span>}
                      </div>
                      <button
                        className="doc-detail-panel__clear-btn"
                        onClick={() => removeTag(tag.id)}
                        title="Clear filter"
                      >×</button>
                    </div>
                    <ul className="doc-detail-panel__list">
                      {getSortedTopics(siblings, tag.context).map((topic) => {
                        const childFileCount = countsLoading ? null : ((topicCounts[tag.context]?.children || []).find(c => c.id === topic.id)?.count || 0);
                        return (
                          <li
                            key={topic.id}
                            className={`doc-detail-panel__item ${tag.id === topic.id ? 'doc-detail-panel__item--active' : ''}`}
                            onClick={() => toggleTag(topic)}
                          >
                            {topic.name}
                            {childFileCount !== null && <span className="doc-detail-panel__count"> ({childFileCount})</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
            {/* Toggle Detail Panels Button */}
            <div className="doc-detail-toggle-container">
              <div
                onClick={() => setDetailPanelsCollapsed((prev) => !prev)}
                className={`doc-detail-toggle-tab ${detailPanelsCollapsed ? 'doc-detail-toggle-tab--collapsed' : ''}`}
                title={detailPanelsCollapsed ? 'Expand panels' : 'Collapse panels'}
              >
                {!detailPanelsCollapsed ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                )}
              </div>
            </div>
          </>
        )}
        <div className="doc-main">
          {(loading && page === 1) ? (
            <div className="flex items-center justify-center h-full text-gray-400 gap-2">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading...</span>
            </div>
          ) : (
            (() => {
              // ── Determine Display Files ──
              const displayFiles = files;

              return (
                <>
                  <div className="doc-horizontal-topics">
                    {!showRecent && Object.entries(topicsByContext)
                      .sort(([a], [b]) => {
                        if (a === 'Operation') return -1;
                        if (b === 'Operation') return 1;
                        return a.localeCompare(b);
                      })
                      .filter(([context]) => {
                        if (activeTags.length >= 3) return false;
                        if (rootTag && rootTag.context === context) return false;
                        return !activeTags.some(t => t.context === context);
                      })
                      .map(([context, children]) => {
                        const isDropdownOpen = activeDropdown === context;
                        const dynamicCount = countsLoading ? null : (topicCounts[context]?.total || 0);
                        return (
                          <div key={context} className="doc-horizontal-topics__group">
                            <button
                              onClick={() => setActiveDropdown(isDropdownOpen ? null : context)}
                              className="doc-horizontal-topics__btn"
                            >
                              {context}{dynamicCount !== null ? ` (${dynamicCount})` : ''}
                              <FontAwesomeIcon icon={faChevronDown} className={`doc-horizontal-topics__chevron ${isDropdownOpen ? 'doc-horizontal-topics__chevron--open' : ''}`} />
                            </button>

                            {isDropdownOpen && (
                              <>
                                <div className="doc-dropdown-backdrop" onClick={() => setActiveDropdown(null)}></div>
                                <div className="doc-horizontal-topics__dropdown">
                                  <div className="doc-horizontal-topics__dropdown-header">
                                    Select {context}{dynamicCount !== null ? ` (${dynamicCount})` : ''}
                                  </div>
                                  <ul className="doc-horizontal-topics__dropdown-list">
                                    {getSortedTopics(children, context).map(child => {
                                      const childDynamic = countsLoading ? null : ((topicCounts[context]?.children || []).find(c => c.id === child.id)?.count || 0);
                                      return (
                                        <li
                                          key={child.id}
                                          onClick={() => {
                                            toggleTag(child);
                                            setActiveDropdown(null);
                                            setPage(1);
                                            setHasMore(true);
                                          }}
                                          className="doc-tree__child doc-horizontal-topics__dropdown-item"
                                        >
                                          {child.name}
                                          {childDynamic !== null && <span className="doc-horizontal-topics__dropdown-count">({childDynamic})</span>}
                                        </li>
                                      )
                                    })}
                                  </ul>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    {!showRecent && Object.entries(topicsByContext).filter(([context]) => {
                      if (rootTag && rootTag.context === context) return false;
                      return !activeTags.some(t => t.context === context);
                    }).length === 0 && (
                        <span className="doc-horizontal-topics__empty-message">All topics selected</span>
                      )}

                    <div className="doc-horizontal-topics__right-container">
                      {showRecent && (
                        <div className="doc-search-wrapper">
                          <FontAwesomeIcon icon={faSearch} className="doc-search-icon" />
                          <input
                            type="text"
                            placeholder="Search recent files..."
                            value={recentSearchQuery}
                            onChange={(e) => setRecentSearchQuery(e.target.value)}
                            className="doc-search-input"
                          />
                        </div>
                      )}
                      <div className="w-20 h-8 border-[#ffa270] border-2 flex rounded overflow-hidden">
                        <div
                          onClick={() => { setViewMode("list"); localStorage.setItem("view", "list"); }}
                          className={`flex-1 flex justify-center items-center cursor-pointer transition-colors ${viewMode === "list" ? "bg-[#ffa270]" : "bg-transparent"}`}
                          title="List View"
                        >
                          <svg
                            className={`w-4 h-4 ${viewMode === "list" ? "fill-white" : "fill-[#ffa270]"}`}
                            viewBox="0 0 1024 1024"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M892.928 128q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0zM892.928 448.512q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0zM892.928 769.024q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0z"></path>
                          </svg>
                        </div>
                        <div
                          onClick={() => { setViewMode("grid"); localStorage.setItem("view", "grid"); }}
                          className={`flex-1 flex justify-center items-center cursor-pointer transition-colors ${viewMode === "grid" ? "bg-[#ffa270]" : "bg-transparent"}`}
                          title="Grid View"
                        >
                          <svg
                            className={`w-4 h-4 ${viewMode === "grid" ? "fill-white" : "fill-[#ffa270]"}`}
                            viewBox="0 0 1024 1024"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M64 160a96 96 0 0 1 96-96h224a96 96 0 0 1 96 96v224a96 96 0 0 1-96 96H160a96 96 0 0 1-96-96V160z m0 480a96 96 0 0 1 96-96h224a96 96 0 0 1 96 96v224a96 96 0 0 1-96 96H160a96 96 0 0 1-96-96V640z m576-96a96 96 0 0 0-96 96v224a96 96 0 0 0 96 96h224a96 96 0 0 0 96-96V640a96 96 0 0 0-96-96H640zM640 64h224q96 0 96 96v224q0 96-96 96H640q-96 0-96-96V160q0-96 96-96z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Bottom Section: Files ── */}
                  <div className="doc-split-bottom">

                    {/* ── List View ── */}
                    {viewMode === 'list' && (
                      <div className="doc-table-wrapper">
                        <table className="doc-table">
                          <thead>
                            <tr>
                              <th className="doc-table__col-name">Name</th>
                              <th className="doc-table__col-modified">Last modified</th>
                              <th className="doc-table__col-created">Created</th>
                              <th className="doc-table__col-type">Type</th>
                              <th className="doc-table__col-size">Size</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // ── Shared row renderer ──
                              const renderRow = (file) => {
                                return (
                                  <tr key={file.item_id} onClick={() => toggleSelect(file.item_id)} onDoubleClick={(e) => { e.stopPropagation(); handleOpenFile(file); }}>
                                    <td>
                                      <div className="doc-file-name">
                                        <span className="doc-file-name__icon">
                                          {file.is_folder ? (
                                            <img className="w-4 h-4" src={folderIcon} alt="" />
                                          ) : (
                                            getFileTypeIcon(file.item_name, coolApps, "w-4 h-4", file.item_type)
                                          )}
                                        </span>
                                        <span className="doc-file-name__text">{file.item_name}</span>
                                        {file.is_favour && (
                                          <FontAwesomeIcon icon={faHeart} className="doc-file-name__fav" />
                                        )}
                                      </div>
                                    </td>
                                    <td>{formatDate(file.modified_time)}</td>
                                    <td>{formatDate(file.created_time)}</td>
                                    <td className="doc-type-cell">
                                      {file.is_folder ? 'Folder' : (file.item_type || '').replace(/^\./, '').toUpperCase()}
                                    </td>
                                    <td className="doc-size-cell">{formatSize(file.item_size)}</td>
                                  </tr>
                                );
                              };

                              // ── Recent view ──
                              if (showRecent) {
                                const filteredRecent = recentSearchQuery
                                  ? recentFiles.filter(f => f.item_name.toLowerCase().includes(recentSearchQuery.toLowerCase()))
                                  : recentFiles;

                                if (filteredRecent.length === 0) {
                                  return (
                                    <tr>
                                      <td colSpan={5} className="doc-table__empty">No recent files</td>
                                    </tr>
                                  );
                                }
                                return filteredRecent.map(renderRow);
                              }

                              // ── Normal filtered view ──
                              if (displayFiles.length === 0) {
                                return (
                                  <tr>
                                    <td colSpan={5} className="doc-table__empty">
                                      {(activeTags.length > 0 || rootTag) ? 'No files match exactly these filters' : 'No untagged files found'}
                                    </td>
                                  </tr>
                                );
                              }

                              return displayFiles.map(renderRow);
                            })()}
                          </tbody>
                        </table>
                        {/* Infinite Scroll Observer Target */}
                        {!showRecent && !loading && hasMore && (
                          <div ref={observerTargetRef} className="doc-scroll-observer" />
                        )}
                        {/* Loading spinner for subsequent pages */}
                        {!showRecent && loading && page > 1 && (
                          <div className="doc-load-more">
                            <FontAwesomeIcon icon={faSpinner} spin /> Loading more...
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Grid View ── */}
                    {viewMode === 'grid' && (
                      <div className="doc-grid-wrapper">
                        {(() => {
                          const renderGridCard = (file) => {
                            const isSelected = selectedIds.has(file.item_id);
                            const fileSize = Number(file.item_size || 0);
                            const isImage = !file.is_folder && isImageFile(file.item_name) && fileSize > 0 && fileSize <= 2 * 1024 * 1024;

                            return (
                              <div
                                key={file.item_id}
                                className={`doc-grid__card ${isSelected ? 'doc-grid__card--selected' : ''}`}
                                onClick={() => toggleSelect(file.item_id)}
                                onDoubleClick={(e) => { e.stopPropagation(); handleOpenFile(file); }}
                              >
                                <div className="doc-grid__preview">
                                  {isImage ? (
                                    <img
                                      src={`/filer/download/${encodeURIComponent(file.item_id)}`}
                                      alt={file.item_name}
                                      className="doc-grid__preview-img"
                                      loading="lazy"
                                      decoding="async"
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                    />
                                  ) : null}
                                  <div
                                    className="doc-grid__preview-icon"
                                    style={{
                                      display: isImage ? 'none' : 'flex',
                                    }}
                                  >
                                    {file.is_folder ? (
                                      <img className="w-12 h-12" src={folderIcon} alt="" />
                                    ) : (
                                      getFileTypeIcon(file.item_name, coolApps, "w-12 h-12 text-5xl", file.item_type)
                                    )}
                                  </div>
                                  {file.is_favour && (
                                    <FontAwesomeIcon icon={faHeart} className="doc-grid__card-fav" />
                                  )}
                                </div>
                                <div className="doc-grid__info">
                                  <span className="doc-grid__info-icon">
                                    {file.is_folder ? (
                                      <img className="w-3.5 h-3.5" src={folderIcon} alt="" />
                                    ) : (
                                      getFileTypeIcon(file.item_name, coolApps, "w-3.5 h-3.5", file.item_type)
                                    )}
                                  </span>
                                  <span className="doc-grid__info-name" title={file.item_name}>{file.item_name}</span>
                                </div>
                              </div>
                            );
                          };

                          if (showRecent) {
                            const filteredRecent = recentSearchQuery
                              ? recentFiles.filter(f => f.item_name.toLowerCase().includes(recentSearchQuery.toLowerCase()))
                              : recentFiles;

                            if (filteredRecent.length === 0) {
                              return (
                                <div className="doc-grid__empty">
                                  No recent files
                                </div>
                              );
                            }
                            return (
                              <div className="doc-grid">
                                {filteredRecent.map(renderGridCard)}
                              </div>
                            );
                          }

                          if (displayFiles.length === 0) {
                            return (
                              <div className="doc-grid__empty">
                                {(activeTags.length > 0 || rootTag) ? 'No files match exactly these filters' : 'No untagged files found'}
                              </div>
                            );
                          }

                          return (
                            <div className="doc-grid">
                              {displayFiles.map(renderGridCard)}
                            </div>
                          );
                        })()}
                        {/* Infinite Scroll Observer Target */}
                        {!showRecent && !loading && hasMore && (
                          <div ref={observerTargetRef} className="doc-scroll-observer" />
                        )}
                        {/* Loading spinner for subsequent pages */}
                        {!showRecent && loading && page > 1 && (
                          <div className="doc-grid__load-more">
                            <FontAwesomeIcon icon={faSpinner} spin /> Loading more...
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bottom Bar */}
                    <div className="doc-bottom-bar">

                      {/* Total Count Widget */}
                      {totalCount > 0 && (
                        <div className="doc-bottom-bar__matches">
                          <span>Showing {files.length} of {totalCount} total matches</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
}
